import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { schoolRequestsApi } from '../api/school-requests.api';
import { formateazaDataIstoric, textIntrareIstoric } from '../utils/istoric-profesori';

interface IstoricProfesoriDialogProps {
  /** Scoala al carei istoric se arata; `null` = dialog inchis. */
  codSiiirUnitate: string | null;
  numeScoala?: string;
  onClose: () => void;
}

/**
 * B4: cine a fost profesorul scolii, cronologic. Intrarile „din cereri" sunt deduse din cererile
 * aprobate pe care jurnalul nu le-a inregistrat (per cerere): aprobarile de dinainte de jurnal
 * (2026-09-24) si reconfirmarile aceluiasi profesor. De aceea nota: schimbarile facute de admin
 * inainte de jurnal nu au lasat urma.
 */
export function IstoricProfesoriDialog({ codSiiirUnitate, numeScoala, onClose }: IstoricProfesoriDialogProps) {
  const deschis = codSiiirUnitate !== null;
  const { data, isLoading, isError } = useQuery({
    queryKey: ['istoric-profesori', codSiiirUnitate],
    queryFn: () => schoolRequestsApi.getIstoricProfesori(codSiiirUnitate!),
    enabled: deschis,
  });

  return (
    <Dialog open={deschis} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Istoric profesori</DialogTitle>
          <DialogDescription>
            {/* La inchidere codul devine null inaintea animatiei: fara el, nu se afiseaza „Cod SIIIR " gol. */}
            {[numeScoala, codSiiirUnitate && `Cod SIIIR ${codSiiirUnitate}`].filter(Boolean).join(' · ')}
          </DialogDescription>
        </DialogHeader>

        {isLoading && <p className="text-sm text-muted-foreground">Se încarcă…</p>}
        {/* role="alert": eroarea apare dupa deschidere, fara mutare de focus - fara el nu se anunta. */}
        {isError && <p role="alert" className="text-sm text-error">Istoricul nu a putut fi încărcat.</p>}
        {data && data.length === 0 && (
          <p className="text-sm text-muted-foreground">Nicio schimbare de profesor înregistrată.</p>
        )}

        {data && data.length > 0 && (
          // role="list": Safari scoate semantica de lista de pe <ol> cu `display: flex`.
          <ol role="list" className="flex flex-col gap-3 border-l border-border pl-4">
            {data.map((intrare, idx) => {
              const dinCereri = intrare.sursa === 'DIN_CERERI';
              return (
                <li key={idx} className={dinCereri ? 'text-muted-foreground' : undefined}>
                  <div className="text-xs tabular-nums">
                    {formateazaDataIstoric(intrare.data as unknown as string)}
                    {intrare.editieNume ? ` · ${intrare.editieNume}` : ''}
                    {dinCereri && <span className="ml-2 rounded bg-muted px-1.5 py-0.5">din cereri</span>}
                  </div>
                  <div className="text-sm">{textIntrareIstoric(intrare)}</div>
                  {intrare.facutDeNume && !dinCereri && (
                    <div className="text-xs text-muted-foreground">de {intrare.facutDeNume}</div>
                  )}
                </li>
              );
            })}
          </ol>
        )}

        <p className="text-xs text-muted-foreground">
          Intrările „din cereri” sunt deduse din cererile aprobate pe care istoricul nu le-a înregistrat: aprobările de dinainte de introducerea istoricului și reconfirmările aceluiași profesor. Schimbările făcute de admin înainte de introducerea istoricului nu au lăsat urmă.
        </p>
      </DialogContent>
    </Dialog>
  );
}
